/**
 * «الطلاب الجدد» (owner decision 2026-11-03): students who signed up from the app, with the program they
 * chose. Placing one in a circle enrolls them and approves that program as their plan, from the chosen day.
 */
import { useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { UserCheck } from "lucide-react";
import type { CircleListResponse, Gender, Registration, RegistrationsResponse } from "@mubin/contracts";
import { Badge, Empty, ErrorNote, Field, Loading, Modal, PageTitle, Tabs, useSubmit } from "../components/ui";
import { api, errorText, send } from "../lib/api";
import { personGenderLabel } from "../lib/labels";
import { useMe } from "../lib/session";

type Status = "pending" | "placed";
const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Riyadh" });
const dateAr = (iso: string) =>
  new Date(iso).toLocaleDateString("ar-SA-u-ca-islamic-umalqura", { day: "numeric", month: "long" });

export function NewStudentsPage() {
  const { data: me } = useMe();
  const [status, setStatus] = useState<Status>("pending");
  const [placing, setPlacing] = useState<Registration | null>(null);
  const list = useQuery({
    queryKey: ["registrations", me?.activeRole, status],
    queryFn: () => api<RegistrationsResponse>(`/v1/student-registrations?status=${status}`),
  });

  return (
    <>
      <PageTitle
        title="الطلاب الجدد"
        sub="سجّلوا من التطبيق وانفتحت حساباتهم، ومع كل طالب خطته التي اختارها. أسنده لحلقة فتُعتمد خطته ويبدأ."
      />
      <section className="card">
        <div className="toolbar">
          <Tabs
            label="الحالة"
            value={status}
            onChange={setStatus}
            items={[
              { value: "pending", label: "بانتظار الإسناد" },
              { value: "placed", label: "أُسندوا" },
            ]}
          />
        </div>
        {list.isPending ? (
          <Loading />
        ) : list.isError ? (
          <Empty title="تعذر التحميل">{errorText(list.error)}</Empty>
        ) : list.data.registrations.length === 0 ? (
          <Empty title={status === "pending" ? "لا يوجد طلاب بانتظار الإسناد" : "لا يوجد طلاب"} />
        ) : (
          <div className="reg-list">
            {list.data.registrations.map((r) => (
              <article key={r.id} className="reg-card">
                <header>
                  <div>
                    <b>{r.student.name}</b>
                    <span className="muted small">
                      {r.student.gender ? personGenderLabel[r.student.gender] : ""}
                      {r.student.birthYear ? ` · مواليد ${r.student.birthYear}` : ""}
                      {" · "}
                      <bdi dir="ltr">{r.student.phone}</bdi>
                    </span>
                  </div>
                  <span className="muted small">سجّل {dateAr(r.createdAt)}</span>
                </header>
                <ul>
                  {r.summary.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
                <footer>
                  {r.status === "pending" ? (
                    <button className="btn btn-primary" onClick={() => setPlacing(r)}>
                      <UserCheck size={18} strokeWidth={1.75} /> إسناد لحلقة واعتماد الخطة
                    </button>
                  ) : (
                    <Badge tone="success">{r.circle ? `في ${r.circle.name}` : "أُسند"}</Badge>
                  )}
                </footer>
              </article>
            ))}
          </div>
        )}
      </section>
      {placing && <PlaceModal registration={placing} onClose={() => setPlacing(null)} />}
    </>
  );
}

function PlaceModal({ registration, onClose }: { registration: Registration; onClose: () => void }) {
  const qc = useQueryClient();
  const { busy, error, run } = useSubmit();
  const [circleId, setCircleId] = useState("");
  const [from, setFrom] = useState(today());
  const circles = useQuery({
    queryKey: ["circles", "all"],
    queryFn: () => api<CircleListResponse>("/v1/circles"),
  });
  // An existing account that signed up may have no gender yet: chosen here, before the circle.
  const missing = !registration.student.gender;
  const [picked, setPicked] = useState<Gender | "">("");
  const gender = registration.student.gender ?? (picked || null);
  const choices = (circles.data?.circles ?? []).filter(
    (c) => !c.archivedAt && (!c.gender || !gender || c.gender === gender),
  );

  function submit(e: FormEvent) {
    e.preventDefault();
    void run(async () => {
      await send("POST", `/v1/student-registrations/${registration.id}/place`, {
        circleId,
        from,
        ...(missing && picked ? { gender: picked } : {}),
      });
      await Promise.all(
        ["registrations", "circles", "users", "overview"].map((k) => qc.invalidateQueries({ queryKey: [k] })),
      );
      onClose();
    }, errorText);
  }

  return (
    <Modal title={`إسناد ${registration.student.name}`} open onClose={onClose} wide>
      <form className="form" onSubmit={submit}>
        <ErrorNote error={error} />
        <ul className="reg-summary">
          {registration.summary.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
        {missing && (
          <Field id="rg-gender" label="جنس الطالب" hint="لم يُحدد في حسابه؛ يلزم للحلقات المخصصة لجنس واحد">
            <select
              id="rg-gender"
              value={picked}
              onChange={(e) => {
                setPicked(e.target.value as Gender | "");
                setCircleId("");
              }}
              required
            >
              <option value="">اختر</option>
              <option value="male">{personGenderLabel.male}</option>
              <option value="female">{personGenderLabel.female}</option>
            </select>
          </Field>
        )}
        <Field id="rg-circle" label="الحلقة" hint="تظهر الحلقات المناسبة لجنس الطالب، ومعلموها وعدد طلابها">
          <select id="rg-circle" value={circleId} onChange={(e) => setCircleId(e.target.value)} required>
            <option value="">اختر الحلقة</option>
            {choices.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} · {c.branchName}
                {c.teachers.length ? ` · ${c.teachers.map((t) => t.name).join("، ")}` : ""} · {c.studentCount}
                {c.capacity ? `/${c.capacity}` : ""} طالب
              </option>
            ))}
          </select>
        </Field>
        <Field id="rg-from" label="تبدأ الخطة من" hint="أول يوم يظهر فيه ورده">
          <input id="rg-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} required />
        </Field>
        <p className="notice small">تُعتمد خطة الطالب كما اختارها، ويصله إشعار بأنه أُسند لحلقته.</p>
        <div className="form-actions">
          <button className="btn btn-primary" disabled={busy || !circleId || (missing && !picked)}>
            {busy ? "جارٍ الإسناد…" : "إسناد واعتماد الخطة"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
